/**
 * Promoter Selector Component
 * Searchable dropdown to select a promoter organization for events
 * Filters organizations by organization_type = 'promoter' and status = 'active'
 * Shows inactive promoters only if they're already selected (for legacy events)
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { supabase } from "../../services/supabase";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";

interface Organization {
  id: string;
  name: string;
  organization_type: string | null;
  status: string;
}

interface PromoterSelectorProps {
  value: string | null; // promoter_id
  onChange: (promoterId: string | null, promoterName: string | null) => void;
  disabled?: boolean;
}

const PromoterSelector: React.FC<PromoterSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  // Fetch active promoter organizations (organization_type = 'promoter' and status = 'active')
  const { data: promoters, isLoading } = useQuery({
    queryKey: ["promoter-organizations-active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, organization_type, status")
        .eq("organization_type", "promoter")
        .eq("status", "active")
        .eq("is_archived", false)
        .order("name");

      if (error) {
        console.warn("Error fetching promoter organizations:", error);
        return [];
      }
      return (data || []) as Organization[];
    },
  });

  // Get selected promoter details (fetches regardless of status for legacy events)
  const { data: selectedPromoter } = useQuery({
    queryKey: ["promoter-organization", value],
    queryFn: async () => {
      if (!value) {
        return null;
      }
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, status")
        .eq("id", value)
        .single();

      if (error) {
        console.warn("Error fetching selected promoter:", error);
        return null;
      }
      return data as { id: string; name: string; status: string };
    },
    enabled: !!value,
  });

  const filteredPromoters =
    promoters?.filter((org) =>
      org.name.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];

  const handleSelect = (org: Organization) => {
    onChange(org.id, org.name);
    setSearchTerm("");
    setShowDropdown(false);
  };

  const handleClear = () => {
    onChange(null, null);
    setSearchTerm("");
  };

  const isSelectedPromoterInactive =
    selectedPromoter && selectedPromoter.status !== "active";

  return (
    <div className="relative">
      <div className="relative">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          onKeyDown={preventEnterSubmit}
          type="text"
          value={selectedPromoter ? selectedPromoter.name : searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (value) {
              onChange(null, null);
            }
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          placeholder="Search promoter organizations..."
          disabled={disabled}
          className={`w-full pl-10 pr-10 py-2 border rounded-md focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 ${
            isSelectedPromoterInactive
              ? "border-amber-300 bg-amber-50"
              : "border-gray-300"
          }`}
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}
      </div>
      {isSelectedPromoterInactive && (
        <p className="mt-1 text-xs text-amber-600">
          This promoter is currently inactive. Select a different promoter or
          keep this one for historical reference.
        </p>
      )}

      {showDropdown && !value && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500">Loading...</div>
          ) : filteredPromoters.length > 0 ? (
            filteredPromoters.map((org) => (
              <button
                key={org.id}
                type="button"
                onClick={() => handleSelect(org)}
                className="w-full px-4 py-3 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-gray-900">{org.name}</div>
                <div className="text-xs text-gray-500">Promoter</div>
              </button>
            ))
          ) : (
            <div className="p-4 text-center text-gray-500">
              {searchTerm
                ? `No promoters found matching "${searchTerm}"`
                : "No promoter organizations available"}
            </div>
          )}
        </div>
      )}

      {!value && promoters?.length === 0 && !isLoading && (
        <p className="mt-1 text-xs text-amber-600">
          No promoter organizations found. Add organizations with "Promoter"
          type first.
        </p>
      )}
    </div>
  );
};

export default PromoterSelector;
